import React, { useState, useMemo } from "react";
import { css } from "@emotion/css";
import type {
  ArcDomainRecommendation,
  DomainRecommendationTier,
} from "../../types/arcGenerationSession";
import {
  useDomainCatalog,
  type Domain,
} from "../../data/domainCatalog";
import { MathText } from "../../components/MathMarkdown";

/**
 * Domain recommendation screen.
 *
 *   - A large "Top recommendation" card for the LLM's best_start
 *     pick, with a "See more" toggle that expands to whyItMatters
 *     + concept chips inline.
 *   - An "Explore other domains" grid of compact tiles for every
 *     other domain in the catalog.  Tapping a tile opens a modal
 *     showing whyItMatters + concept chips + a "Choose this
 *     domain" CTA.
 *   - The persistent bottom-bar Continue button (rendered by
 *     ``ArcGenerationSession``) reflects the current selection,
 *     so the in-card / in-modal CTAs only need to call
 *     ``onSelect(domainId)``.
 *
 * Concept chips and SVG icons both come from the catalog JSON via
 * ``../../data/domainCatalog`` — single source of truth.
 */

// ── Tier visual config ──────────────────────────────────────────────

const TIER_CONFIG: Record<DomainRecommendationTier, {
  label: string;
  accent: string;            // border / glow accent
  badgeBg: string;
  badgeText: string;
}> = {
  best_start: {
    label: "TOP RECOMMENDATION",
    accent: "var(--color-success-text)",
    badgeBg: "var(--color-success-bg)",
    badgeText: "var(--color-success-text)",
  },
  strong_alternative: {
    label: "STRONG ALTERNATIVE FOR YOU",
    accent: "#eab308",
    badgeBg: "#3d2e00",
    badgeText: "#eab308",
  },
  explore_later: {
    label: "",
    accent: "var(--color-border)",
    badgeBg: "",
    badgeText: "",
  },
};

// ── Styles ──────────────────────────────────────────────────────────

const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const catalogLoadingStyle = css`
  padding: 24px;
  text-align: center;
  font-size: 0.9em;
  color: var(--color-text-muted);
  border: 1px dashed var(--color-border);
  border-radius: 12px;
`;

const catalogErrorStyle = css`
  padding: 16px 18px;
  font-size: 0.9em;
  color: var(--color-error);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-error);
  border-radius: 12px;
  line-height: 1.5;
`;

const topCardStyle = css`
  position: relative;
  border: 1px solid var(--color-success-border);
  border-radius: 16px;
  background: var(--color-success-bg);
  padding: 22px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const tierBadgeStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 4px 10px;
  border-radius: 999px;
  align-self: flex-start;
`;

const topHeaderRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const topIconBoxStyle = css`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: var(--color-success-text);
  color: var(--color-success-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  & > svg { width: 100%; height: 100%; display: block; }
`;

const topTitleStyle = css`
  font-size: 1.25em;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.25;
`;

const topPromiseStyle = css`
  font-size: 0.95em;
  color: var(--color-text-secondary);
  margin-top: 4px;
  line-height: 1.5;
`;

const topCtaStyle = css`
  width: 100%;
  padding: 13px 18px;
  border: none;
  border-radius: 12px;
  background: var(--color-success-text);
  color: var(--color-success-bg);
  font-size: 0.98em;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const seeMoreToggleStyle = css`
  background: none;
  border: none;
  color: var(--color-success-text);
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
  align-self: flex-start;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const expandedSectionStyle = css`
  border-top: 1px solid var(--color-success-border);
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const whyItMattersStyle = css`
  font-size: 0.92em;
  color: var(--color-text-secondary);
  line-height: 1.55;
`;

const conceptsLabelStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const conceptChipsRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const conceptChipStyle = css`
  font-size: 0.82em;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--color-bg-raised);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-subtle);
`;

const exploreSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const exploreHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const exploreLabelStyle = css`
  font-size: 0.78em;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-text-muted);
  font-weight: 700;
`;

const exploreCountStyle = css`
  font-size: 0.78em;
  color: var(--color-text-muted);
`;

const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

const tileStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 12px 14px;
  border-radius: 12px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.05s ease;

  &:hover {
    border-color: var(--color-primary);
    background: var(--color-bg-raised);
  }
  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

const tileSelectedStyle = css`
  border-color: var(--color-primary);
  background: var(--color-primary-muted);
`;

const tileTopRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const tileIconBoxStyle = css`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  /* Soft primary tint so the icon reads clearly against any tile
     background.  --color-primary (saturated blue) on
     --color-primary-muted (low-saturation blue) gives strong
     contrast in both dark and light modes. */
  background: var(--color-primary-muted);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 6px;
  & > svg { width: 100%; height: 100%; display: block; }
`;

const tileIndexStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  color: var(--color-text-muted);
  margin-left: auto;
`;

const tileNameStyle = css`
  font-size: 0.88em;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-text);
`;

const tileTierBadgeStyle = css`
  font-size: 0.62em;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 2px 6px;
  border-radius: 999px;
  text-transform: uppercase;
`;

// ── Modal ───────────────────────────────────────────────────────────

const modalOverlayStyle = css`
  position: fixed;
  inset: 0;
  background: var(--color-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const modalCardStyle = css`
  position: relative;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: 18px;
  max-width: 540px;
  width: 100%;
  max-height: 88vh;
  overflow-y: auto;
  padding: 22px 22px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 20px 60px var(--color-shadow-strong);
`;

const modalCloseStyle = css`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 999px;
  background: var(--color-bg-raised);
  color: var(--color-text-secondary);
  font-size: 1.05em;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: var(--color-bg-subtle); }
`;

const modalHeaderRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding-right: 36px; /* room for the close button */
`;

const modalIconBoxStyle = css`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  /* Same soft-primary tint as the explore tiles, for visual
     continuity when the user opens the modal from a tile. */
  background: var(--color-primary-muted);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  flex-shrink: 0;
  & > svg { width: 100%; height: 100%; display: block; }
`;

const modalTitleStyle = css`
  font-size: 1.15em;
  font-weight: 700;
  line-height: 1.3;
  color: var(--color-text);
`;

const modalPromiseStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
  margin-top: 4px;
  line-height: 1.5;
`;

const modalBodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const fitReasonStyle = css`
  font-size: 0.9em;
  color: var(--color-text);
  background: var(--color-bg-subtle);
  border-left: 3px solid var(--color-primary);
  border-radius: 6px;
  padding: 10px 12px;
  font-style: italic;
  line-height: 1.5;
`;

const modalChooseBtnStyle = css`
  width: 100%;
  padding: 13px 18px;
  border: none;
  border-radius: 12px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.98em;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;

  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

// ── Component ───────────────────────────────────────────────────────

interface DomainStripProps {
  recommendations: ArcDomainRecommendation[];
  selectedDomainId: string | null;
  isStreaming: boolean;
  submitting: boolean;
  onSelect: (domainId: string) => void;
  // Used by the TOP recommendation card to confirm + advance in
  // a single tap — picking the coach's pick shouldn't also
  // require pressing the bottom Continue button.  Other domains
  // still go through the modal + bottom Continue flow.
  onConfirm: (domainId: string) => void;
}

interface ResolvedDomain {
  domain: Domain;
  rec: ArcDomainRecommendation | undefined;
  tier: DomainRecommendationTier;
}

/** Inline-SVG renderer.  The backend ships SVG markup as a string
 * (see ``build_domain_catalog_for_api``); ``dangerouslySetInnerHTML``
 * is the standard React way to render trusted markup.  Trust:
 * the SVG comes from the backend's own filesystem, not user input,
 * so there's no XSS surface. */
const IconBox: React.FC<{ svg: string; boxClassName: string }> = ({
  svg,
  boxClassName,
}) => (
  <span
    className={boxClassName}
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: svg }}
  />
);

export const DomainStrip: React.FC<DomainStripProps> = ({
  recommendations,
  selectedDomainId,
  isStreaming,
  submitting,
  onSelect,
  onConfirm,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [modalDomainId, setModalDomainId] = useState<string | null>(null);
  const { catalog, error } = useDomainCatalog();

  const flatDomains: Domain[] = useMemo(
    () =>
      catalog == null
        ? []
        : catalog.categories.flatMap((c) => c.domains),
    [catalog]
  );

  const recByDomainId = useMemo(() => {
    const map: Record<string, ArcDomainRecommendation> = {};
    for (const r of recommendations) {
      if (r.domainId) map[r.domainId] = r;
    }
    return map;
  }, [recommendations]);

  const resolved: ResolvedDomain[] = useMemo(
    () =>
      flatDomains.map((d) => {
        const rec = recByDomainId[d.domainId];
        const rawTier = rec?.recommendationTier || "";
        const tier: DomainRecommendationTier =
          rawTier in TIER_CONFIG
            ? (rawTier as DomainRecommendationTier)
            : "explore_later";
        return { domain: d, rec, tier };
      }),
    [flatDomains, recByDomainId]
  );

  const topPick = resolved.find((r) => r.tier === "best_start") ?? resolved[0];
  const others = resolved.filter((r) => r !== topPick);

  const modalEntry =
    modalDomainId != null
      ? resolved.find((r) => r.domain.domainId === modalDomainId)
      : null;

  const handleChoose = (domainId: string) => {
    if (isStreaming) return;
    onSelect(domainId);
  };

  const openModal = (domainId: string) => {
    if (isStreaming) return;
    setModalDomainId(domainId);
  };

  const closeModal = () => setModalDomainId(null);

  // Render-time guards: the catalog fetch is async, so the first
  // paint has nothing to show.  Show a skeleton + an error state
  // so the screen never looks blank.
  if (error) {
    return (
      <div
        className={containerStyle}
        data-testid="domain-strip"
        role="alert"
      >
        <div className={catalogErrorStyle}>
          Couldn't load the domain catalog. Please refresh the
          page; if it keeps happening, the catalog endpoint may
          be down.
        </div>
      </div>
    );
  }
  if (catalog == null) {
    return (
      <div
        className={containerStyle}
        data-testid="domain-strip"
        aria-busy="true"
      >
        <div className={catalogLoadingStyle}>Loading domains…</div>
      </div>
    );
  }

  return (
    <div className={containerStyle} data-testid="domain-strip">
      {/* Top recommendation card */}
      <TopRecommendationCard
        entry={topPick}
        expanded={expanded}
        toggleExpanded={() => setExpanded((v) => !v)}
        onConfirm={onConfirm}
        isStreaming={isStreaming}
        submitting={submitting}
      />

      {/* Explore other domains */}
      <div className={exploreSectionStyle}>
        <div className={exploreHeaderStyle}>
          <span className={exploreLabelStyle}>Explore other domains</span>
          <span className={exploreCountStyle}>
            {flatDomains.length} domains total
          </span>
        </div>
        <div className={gridStyle}>
          {others.map((entry, i) => {
            const isSelected =
              selectedDomainId === entry.domain.domainId;
            const tierConfig = TIER_CONFIG[entry.tier];
            return (
              <button
                key={entry.domain.domainId}
                type="button"
                className={`${tileStyle} ${isSelected ? tileSelectedStyle : ""}`}
                onClick={() => openModal(entry.domain.domainId)}
                disabled={isStreaming}
                data-testid={`domain-card-${entry.domain.domainId}`}
              >
                <div className={tileTopRowStyle}>
                  <IconBox
                    svg={entry.domain.iconSvg}
                    boxClassName={tileIconBoxStyle}
                  />
                  {entry.tier === "strong_alternative" && (
                    <span
                      className={tileTierBadgeStyle}
                      style={{
                        background: tierConfig.badgeBg,
                        color: tierConfig.badgeText,
                      }}
                    >
                      Alt
                    </span>
                  )}
                  <span className={tileIndexStyle}>
                    {String(i + 2).padStart(1, "0")}
                  </span>
                </div>
                <div className={tileNameStyle}>{entry.domain.name}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {modalEntry && (
        <DomainDetailModal
          entry={modalEntry}
          onChoose={handleChoose}
          onClose={closeModal}
          isStreaming={isStreaming}
        />
      )}
    </div>
  );
};

// ── Top recommendation card ─────────────────────────────────────────

const TopRecommendationCard: React.FC<{
  entry: ResolvedDomain | undefined;
  expanded: boolean;
  toggleExpanded: () => void;
  // Confirms + advances in one tap — the coach's pick is the
  // happy path and shouldn't require a second Continue press.
  onConfirm: (domainId: string) => void;
  isStreaming: boolean;
  submitting: boolean;
}> = ({ entry, expanded, toggleExpanded, onConfirm, isStreaming, submitting }) => {
  if (!entry) return null;
  const { domain, rec } = entry;
  return (
    <div
      className={topCardStyle}
      data-testid={`top-recommendation-${domain.domainId}`}
    >
      <span
        className={tierBadgeStyle}
        style={{
          background: TIER_CONFIG.best_start.badgeBg,
          color: TIER_CONFIG.best_start.badgeText,
        }}
        data-testid="top-recommendation-badge"
      >
        ★ TOP RECOMMENDATION
      </span>
      <div className={topHeaderRowStyle}>
        <IconBox svg={domain.iconSvg} boxClassName={topIconBoxStyle} />
        <div>
          <div className={topTitleStyle}>{domain.name}</div>
          <div className={topPromiseStyle}>{domain.promise}</div>
        </div>
      </div>

      <button
        type="button"
        className={seeMoreToggleStyle}
        onClick={toggleExpanded}
        data-testid="top-recommendation-see-more"
      >
        {expanded ? "Hide details ▲" : "See more ▾"}
      </button>

      {expanded && (
        <div
          className={expandedSectionStyle}
          data-testid="top-recommendation-expanded"
        >
          <div className={whyItMattersStyle}>
            {rec?.fitReason ? (
              <MathText text={rec.fitReason} />
            ) : (
              domain.whyItMatters
            )}
          </div>
          {rec?.fitReason && (
            <div className={whyItMattersStyle}>{domain.whyItMatters}</div>
          )}
          <div>
            <div className={conceptsLabelStyle}>You'll touch</div>
            <div
              className={conceptChipsRowStyle}
              data-testid="top-recommendation-concepts"
            >
              {domain.concepts.map((c) => (
                <span key={c} className={conceptChipStyle}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className={topCtaStyle}
        onClick={() => onConfirm(domain.domainId)}
        disabled={isStreaming || submitting}
        data-testid="top-recommendation-continue"
      >
        {submitting
          ? "Continuing…"
          : "Continue with coach recommendation →"}
      </button>
    </div>
  );
};

// ── Domain detail modal ─────────────────────────────────────────────

const DomainDetailModal: React.FC<{
  entry: ResolvedDomain;
  onChoose: (domainId: string) => void;
  onClose: () => void;
  isStreaming: boolean;
}> = ({ entry, onChoose, onClose, isStreaming }) => {
  const { domain, rec, tier } = entry;
  const tierConfig = TIER_CONFIG[tier];

  return (
    <div
      className={modalOverlayStyle}
      onClick={onClose}
      data-testid="domain-detail-modal"
    >
      <div
        className={modalCardStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          className={modalCloseStyle}
          onClick={onClose}
          aria-label="Close"
          data-testid="domain-detail-modal-close"
        >
          ×
        </button>

        <div className={modalHeaderRowStyle}>
          <IconBox svg={domain.iconSvg} boxClassName={modalIconBoxStyle} />
          <div>
            {tierConfig.label && (
              <span
                className={tierBadgeStyle}
                style={{
                  background: tierConfig.badgeBg,
                  color: tierConfig.badgeText,
                  marginBottom: 4,
                }}
              >
                ★ {tierConfig.label}
              </span>
            )}
            <div className={modalTitleStyle}>{domain.name}</div>
            <div className={modalPromiseStyle}>{domain.promise}</div>
          </div>
        </div>

        <div className={modalBodyStyle}>
          {rec?.fitReason && (
            <div className={fitReasonStyle}>
              <MathText text={rec.fitReason} />
            </div>
          )}
          <div className={whyItMattersStyle}>{domain.whyItMatters}</div>
          <div>
            <div className={conceptsLabelStyle}>Concepts you'll touch</div>
            <div
              className={conceptChipsRowStyle}
              data-testid="domain-detail-modal-concepts"
            >
              {domain.concepts.map((c) => (
                <span key={c} className={conceptChipStyle}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          className={modalChooseBtnStyle}
          onClick={() => {
            onChoose(domain.domainId);
            onClose();
          }}
          disabled={isStreaming}
          data-testid="domain-detail-modal-choose"
        >
          Choose this domain →
        </button>
      </div>
    </div>
  );
};

